import { plateFont } from '../../utils/fonts';
import * as React from 'react';
import { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import BuildIcon from '@mui/icons-material/Build';
import EventIcon from '@mui/icons-material/Event';
import { withTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import VehicleDetailsDialog from './VehicleDetailsDialog';
import { formatDate } from '../../utils';
import { usePaginatedList } from '../../hooks/usePaginatedList';
import PaginationControls from '../shared/PaginationControls';
import {
  useListPreferences,
  SearchInput,
} from '../shared/ListFilters';
import DataTable from '../shared/DataTable';

const STORAGE_KEY = 'vehicles_list_preferences';

const defaultPreferences = {
  sortField: 'last_maintenance_date',
  sortDirection: 'desc',
  searchQuery: '',
  filters: {},
};

const MainVehiclesView = ({ t }) => {
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep link from the global search (Ctrl+K): /vehicles?vehicle=<id>. Same recipe as
  // MainCustomersView's ?customer= param: open the dialog, then drop the param from the URL.
  React.useEffect(() => {
    const vehicleId = searchParams.get('vehicle');
    if (!vehicleId) return;
    setSelectedVehicleId(Number(vehicleId));
    setDetailsDialogOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('vehicle');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('vehicle')]);

  // Use localStorage-persisted preferences
  const {
    preferences,
    updatePreferences,
    handleSort: handleLocalSort,
  } = useListPreferences(STORAGE_KEY, defaultPreferences);

  // Use server-side pagination hook
  const {
    data: vehicles,
    loading,
    error,
    page,
    pageSize,
    totalCount,
    totalPages,
    setPage,
    setPageSize,
    searchQuery,
    setSearchQuery,
    sortField,
    sortDirection,
    handleSort,
    setSort,
    refetch,
  } = usePaginatedList('/api/vehicles/', {
    initialFilters: {},
    defaultSortField: preferences.sortField,
    defaultSortDirection: preferences.sortDirection,
    defaultPageSize: 50,
    initialSearchQuery: preferences.searchQuery || '',
  });

  // Sync search query from localStorage preferences to hook
  React.useEffect(() => {
    setSearchQuery(preferences.searchQuery || '');
  }, [preferences.searchQuery, setSearchQuery]);

  // Wrapper to sync sort to both local preferences and server
  const handleSortWrapper = React.useCallback((field) => {
    handleLocalSort(field);
    handleSort(field);
  }, [handleLocalSort, handleSort]);

  const handleVehicleClick = (vehicle) => {
    setSelectedVehicleId(vehicle.id);
    setDetailsDialogOpen(true);
  };

  // State for error snackbar - cleared when error changes to null
  const [showErrorSnackbar, setShowErrorSnackbar] = React.useState(false);
  const prevError = React.useRef(error);

  React.useEffect(() => {
    // Show snackbar when a new error appears
    if (error && error !== prevError.current) {
      setShowErrorSnackbar(true);
    }
    prevError.current = error;
  }, [error]);

  const handleCloseErrorSnackbar = () => {
    setShowErrorSnackbar(false);
  };

  return (
    <Box data-testid="vehicles-page">
      {/* Error Snackbar - shows error without blocking the UI */}
      <Snackbar
        open={showErrorSnackbar && !!error}
        autoHideDuration={6000}
        onClose={handleCloseErrorSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseErrorSnackbar} severity="error" sx={{ width: '100%' }}>
          {t('Error')}: {error}
        </Alert>
      </Snackbar>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          mb: 3,
        }}
      >
        <Typography
          component="h4"
          variant="h4"
          data-testid="vehicles-heading"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {t('vehicles.title')}
        </Typography>
      </Box>

      {/* Search Row */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 2.5,
          alignItems: { xs: 'stretch', sm: 'center' },
        }}
      >
        <SearchInput
          value={preferences.searchQuery || ''}
          onChange={(value) => updatePreferences({ searchQuery: value })}
          onClear={() => updatePreferences({ searchQuery: '' })}
          placeholder={t('vehicles.searchPlaceholder')}
        />
      </Box>

      {/* Desktop Table */}
      <DataTable
        columns={[
          {
            key: 'icon',
            label: '',
            width: '1%',
            render: () => (
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '8px',
                  bgcolor: 'rgba(49, 130, 206, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'secondary.main',
                }}
              >
                <DirectionsCarIcon fontSize="small" />
              </Box>
            ),
          },
          {
            key: 'registration_number',
            label: t('Registration'),
            sortable: true,
            noWrap: true,
            render: (vehicle) => (
              <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'text.primary', ...plateFont }}>
                {vehicle.registration_number}
              </Typography>
            ),
          },
          {
            key: 'manufacturer',
            label: t('Vehicle'),
            sortable: true,
            sortField: 'manufacturer',
            render: (vehicle) => (
              <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.primary' }}>
                {vehicle.manufacturer} {vehicle.model}
                {vehicle.model_variant && (
                  <Typography component="span" sx={{ color: 'text.secondary', ml: 0.5 }}>
                    {vehicle.model_variant}
                  </Typography>
                )}
              </Typography>
            ),
          },
          {
            key: 'year_model',
            label: t('Year'),
            sortable: true,
            width: '1%',
            noWrap: true,
            render: (vehicle) => (
              <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                {vehicle.year_model || '-'}
              </Typography>
            ),
          },
          {
            key: 'maintenance_count',
            label: t('vehicles.maintenanceCount'),
            sortable: true,
            width: '1%',
            noWrap: true,
            render: (vehicle) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <BuildIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                <Typography sx={{ fontSize: '13px', color: 'text.secondary', fontWeight: 500 }}>
                  {vehicle.maintenance_count}
                </Typography>
              </Box>
            ),
          },
          {
            key: 'last_maintenance_date',
            label: t('vehicles.lastMaintenance'),
            sortable: true,
            width: '1%',
            noWrap: true,
            render: (vehicle) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <EventIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                  {vehicle.last_maintenance_date ? formatDate(vehicle.last_maintenance_date)?.split(' ')[0] : '-'}
                </Typography>
              </Box>
            ),
          },
        ]}
        data={vehicles}
        loading={loading}
        onRowClick={handleVehicleClick}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSortWrapper}
        emptyIcon={<DirectionsCarIcon />}
        emptyTitle={searchQuery ? t('No vehicles match your search') : t('vehicles.noMaintenances')}
        testIdPrefix="vehicle-row"
        tableTestId="vehicles-table"
        pagination={{
          page,
          totalPages,
          totalCount,
          pageSize,
          onPageChange: setPage,
          onPageSizeChange: setPageSize,
        }}
      />

      {/* Mobile Cards - Hidden on desktop */}
      <Box sx={{ display: { xs: 'block', lg: 'none' }, position: 'relative' }}>
        {/* Loading overlay for refetch */}
        {loading && vehicles.length > 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 1,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              pt: 8,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        )}
        {loading && vehicles.length === 0 ? (
          <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        ) : vehicles.length === 0 ? (
          <Paper elevation={0} sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {searchQuery ? t('No vehicles match your search') : t('vehicles.noMaintenances')}
            </Typography>
          </Paper>
        ) : (
          <>
            {vehicles.map((vehicle) => (
              <Paper
                key={vehicle.id}
                data-testid="vehicle-row"
                elevation={0}
                onClick={() => handleVehicleClick(vehicle)}
                sx={{
                  p: 2,
                  mb: 1.5,
                  cursor: 'pointer',
                  transition: 'background-color 0.15s ease',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  {/* Vehicle Icon */}
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '10px',
                      bgcolor: 'rgba(49, 130, 206, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'secondary.main',
                      flexShrink: 0,
                    }}
                  >
                    <DirectionsCarIcon />
                  </Box>

                  {/* Vehicle Info */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography
                        sx={{
                          fontSize: '15px',
                          fontWeight: 600,
                          color: 'text.primary',
                          ...plateFont,
                        }}
                      >
                        {vehicle.registration_number}
                      </Typography>
                      {vehicle.year_model && (
                        <Typography
                          sx={{
                            fontSize: '12px',
                            color: 'text.secondary',
                            bgcolor: 'background.default',
                            px: 1,
                            py: 0.25,
                            borderRadius: '4px',
                          }}
                        >
                          {vehicle.year_model}
                        </Typography>
                      )}
                    </Box>

                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: 'text.primary',
                        mb: 0.5,
                      }}
                    >
                      {vehicle.manufacturer} {vehicle.model}
                    </Typography>

                    {/* Stats */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <BuildIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                        <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                          {vehicle.maintenance_count} {t('maintenances').toLowerCase()}
                        </Typography>
                      </Box>
                      {vehicle.last_maintenance_date && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <EventIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                          <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                            {formatDate(vehicle.last_maintenance_date)?.split(' ')[0]}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Paper>
            ))}

            {/* Mobile Pagination Controls */}
            <PaginationControls
              page={page}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              loading={loading}
            />
          </>
        )}
      </Box>

      <VehicleDetailsDialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        vehicleId={selectedVehicleId}
        onVehicleDeleted={() => {
          setDetailsDialogOpen(false);
          refetch();
        }}
        onVehicleUpdated={() => {
          refetch();
        }}
      />
    </Box>
  );
};

export default withTranslation()(MainVehiclesView);
